import { webPaths } from "@zplanem/web-paths"
import type React from "react"
import { NavLink, useNavigate } from "react-router-dom"
import { useAuth } from "../auth-context"
import { useVisibleProjectTabs } from "../hooks/use-visible-project-tabs"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { MobileMoreMenuItem } from "./mobile-more-menu"
import {
	PROJECT_TABS,
	projectMembersPath,
	projectTabPath,
} from "./project-tabs"
import { PROJECT_TAB_ICONS } from "./tab-icons"

// Members and Budget live in the mobile More menu, not the bottom-bar tabs —
// five tabs is the bar's capacity, and Budget is owner-only besides.
type BottomTab = Exclude<
	(typeof PROJECT_TABS)[number],
	{ key: "members" } | { key: "budget" }
>

const BOTTOM_TABS = PROJECT_TABS.filter(
	(tab): tab is BottomTab => tab.key !== "members" && tab.key !== "budget",
)

/**
 * Project-scoped mobile bottom bar (ZPL-269 revision b), mirroring
 * PropertyBottomNav. Replaces the property bottom bar while inside a
 * project: five project sections as tabs (visualizations uses the short
 * mobile label) plus a More menu carrying Members and the global
 * destinations (Properties / Profile / Sign out). On the Members route no
 * tab is active — it is a menu destination, like Profile.
 */
export const ProjectBottomNav: React.FC<{
	propertyId: string
	projectId: string
}> = ({ propertyId, projectId }) => {
	const { logout } = useAuth()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const navigate = useNavigate()
	// Owner-only Budget: same visibility rule as the desktop tab.
	const showBudget = useVisibleProjectTabs(propertyId).some(
		(tab) => tab.key === "budget",
	)

	return (
		<nav
			aria-label={t.projects.tabs.navLabel}
			className="md:hidden fixed bottom-0 left-0 right-0 h-16 z-30 bg-card border-t border-border flex items-stretch"
		>
			{BOTTOM_TABS.map((tab) => {
				const Icon = PROJECT_TAB_ICONS[tab.key]
				return (
					<NavLink
						key={tab.key}
						to={projectTabPath(propertyId, projectId, tab)}
						end={tab.path === ""}
						className={({ isActive }) =>
							"flex-1 min-w-0 flex flex-col items-center justify-center gap-1 border-t-2 text-xs transition-colors " +
							(isActive
								? "border-ring bg-accent/40 text-foreground"
								: "border-transparent text-muted-foreground hover:text-foreground")
						}
					>
						<Icon />
						<span className="truncate w-full text-center px-0.5">
							{tab.key === "visualizations"
								? t.projects.tabs.visualizationsShort
								: t.projects.tabs[tab.key]}
						</span>
					</NavLink>
				)
			})}

			<MobileMoreMenuItem
				items={[
					...(showBudget
						? [
								{
									label: t.projects.tabs.budget,
									onSelect: () =>
										navigate(webPaths.projectBudget(propertyId, projectId)),
								},
							]
						: []),
					{
						label: t.projects.tabs.members,
						onSelect: () => navigate(projectMembersPath(propertyId, projectId)),
					},
					{
						label: t.sidebar.properties,
						onSelect: () => navigate(webPaths.dashboard()),
					},
					{
						label: t.sidebar.profile,
						onSelect: () => navigate(webPaths.profile()),
					},
					{
						label: t.sidebar.settings,
						onSelect: () => navigate(webPaths.settings()),
					},
					{ label: t.sidebar.signOut, onSelect: logout, tone: "danger" },
				]}
			/>
		</nav>
	)
}
